import React, { useState } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import {
  Button,
  Modal,
  ModalControlModalProps,
  SwitchChipGroup,
  Text,
  useSwitchChipGroup,
} from '@frontend/design-system';
import { useGetLocationsWithReviewsAnalyticsAccess } from '../../hooks';

interface ReviewLocationSelectorModalProps {
  modalProps: ModalControlModalProps;
  onChange: (locationIds: string) => void;
  initialSelectedLocation?: string;
}

export const ReviewLocationSelectorModal = React.memo(
  ({ modalProps, onChange, initialSelectedLocation }: ReviewLocationSelectorModalProps) => {
    const { t } = useTranslation('schedule');
    const { getScopeName } = useAppScopeStore();
    const locationsWithReviewsAnalyticsAccess = useGetLocationsWithReviewsAnalyticsAccess();
    const [selectedLocation, setSelectedLocation] = useState(initialSelectedLocation);
    const isMobile = useMatchMedia({ maxWidth: breakpoints.small.min });

    const switchChipProps = useSwitchChipGroup({
      defaultValue: initialSelectedLocation ? [initialSelectedLocation] : [],
      onChange: (val) => setSelectedLocation(val[0]),
    });

    const handleSave = () => {
      if (!selectedLocation) return;
      onChange(selectedLocation);
      modalProps.onClose();
    };

    return (
      <Modal {...modalProps} minWidth={350} maxWidth={isMobile ? 350 : 728} mountTarget='#root'>
        <Modal.Header onClose={modalProps.onClose}>{t("We've Picked a Location for You")}</Modal.Header>
        <Modal.Body css={{ overflowY: 'unset' }}>
          <Text>
            {t(
              "For your convenience and to optimize our system's performance, we've preselected a relevant location for you to view. Modify the selection below, or continue with our suggestion."
            )}
          </Text>
          <div css={locationSelectorContainerStyles}>
            <SwitchChipGroup {...switchChipProps} singleSelect css={{ flexWrap: 'wrap' }}>
              {locationsWithReviewsAnalyticsAccess.map((locationId) => (
                <SwitchChipGroup.Option key={locationId} value={locationId}>
                  {getScopeName(locationId)}
                </SwitchChipGroup.Option>
              ))}
            </SwitchChipGroup>
          </div>
          <div css={modalFooterContainerStyles}>
            <div css={modalFooterButtonContainerStyles}>
              <Button
                variant='primary'
                disabled={locationsWithReviewsAnalyticsAccess.length === 0}
                onClick={handleSave}
              >
                {t('View')}
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    );
  }
);

ReviewLocationSelectorModal.displayName = 'ReviewLocationSelectorModal';

const modalFooterContainerStyles = css({ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' });

const modalFooterButtonContainerStyles = css({ display: 'flex', marginLeft: theme.spacing(1), gap: 8 });

const locationSelectorContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  width: '100%',
  gap: 8,
  margin: theme.spacing(3, 0),
  flexWrap: 'wrap',
});
